import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  Code
} from "lucide-react";
import { ProcessingRule, ElementVersionProcessingRule } from "@/entities/all";
import { fetchData } from "@/fetchapi/fetchapi";
import { toast } from "sonner";
import ViewProcessingRuleModal from "../processing-rules/ViewProcessingRuleModal";

const ProcessingRulesPipeline = forwardRef(({
  elementVersionId,
  disabled = false,
  onPipelineChange
}, ref) => {
  const [availableRules, setAvailableRules] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [originalPipeline, setOriginalPipeline] = useState([]); // Track original state for comparison
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRuleId, setSelectedRuleId] = useState('');
  const [viewingRule, setViewingRule] = useState(null); // Rule being viewed in modal

  // Expose save method to parent via ref
  useImperativeHandle(ref, () => ({
    savePipeline: async (versionId) => {
      return await savePipelineChanges(versionId);
    },
    hasPendingChanges: () => {
      return hasChanges();
    },
    getPipeline: () => pipeline
  }));

  useEffect(() => {
    loadAvailableRules();
  }, []);

  useEffect(() => {
    if (elementVersionId) {
      loadPipeline();
    } else {
      setPipeline([]);
      setOriginalPipeline([]);
      setIsLoading(false);
    }
  }, [elementVersionId]);

  const loadAvailableRules = async () => {
    try {
      const rules = await ProcessingRule.filter({ status: 'Active' }, 'rule_name');
      setAvailableRules(rules);
    } catch (error) {
      console.error('Error loading processing rules:', error);
    }
  };

  const loadPipeline = async () => {
    if (!elementVersionId) return;

    setIsLoading(true);
    try {
      const pipelineData = await ElementVersionProcessingRule.filter(
        { element_version_id: elementVersionId },
        'execution_order'
      );

      // Enrich with rule details
      const enrichedPipeline = await Promise.all(
        pipelineData.map(async (item) => {
          const rule = availableRules.find(r => r.id === item.processing_rule_id) ||
                       await ProcessingRule.get(`id=${item.processing_rule_id}`);
          return {
            ...item,
            rule
          };
        })
      );

      setPipeline(enrichedPipeline);
      setOriginalPipeline(JSON.parse(JSON.stringify(enrichedPipeline))); // Deep copy for comparison
    } catch (error) {
      console.error('Error loading pipeline:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if there are unsaved changes
  const hasChanges = () => {
    if (pipeline.length !== originalPipeline.length) return true;

    for (let i = 0; i < pipeline.length; i++) {
      const current = pipeline[i];
      const original = originalPipeline[i];

      if (!original) return true;
      if (current.processing_rule_id !== original.processing_rule_id) return true;
      if (current.execution_order !== original.execution_order) return true;
      if (current.is_active !== original.is_active) return true;
      if (current._isNew !== original._isNew) return true;
      if (current._isDeleted !== original._isDeleted) return true;
    }

    return false;
  };

  // Save all pipeline changes to database
  const savePipelineChanges = async (versionId) => {
    const targetVersionId = versionId || elementVersionId;
    if (!targetVersionId) return { success: false, error: 'No version ID' };

    try {
      // Send all active rules in a single batch request
      const activeRules = pipeline.filter(p => !p._isDeleted);
      const rules = activeRules.map(p => ({
        id: p._isNew ? undefined : p.id,
        processing_rule_id: p.processing_rule_id,
        execution_order: p.execution_order,
        is_active: p.is_active
      }));

      await fetchData(`element_version_processing_rule/batch/${targetVersionId}`, {
        method: 'PUT',
        body: JSON.stringify({ rules }),
        headers: { 'Content-Type': 'application/json' }
      });

      // Reload pipeline to get fresh data with proper IDs
      if (targetVersionId === elementVersionId) {
        await loadPipeline();
      }

      return { success: true };
    } catch (error) {
      console.error('Error saving pipeline changes:', error);
      return { success: false, error: error.message };
    }
  };

  // Local-only operations (no database calls)
  const addRuleToPipeline = () => {
    if (!selectedRuleId) return;

    const ruleId = parseInt(selectedRuleId, 10);
    const rule = availableRules.find(r => r.id === ruleId);

    if (!rule) return;

    // Check if already in pipeline (excluding deleted items)
    if (pipeline.some(p => p.processing_rule_id === ruleId && !p._isDeleted)) {
      toast.error('This rule is already in the pipeline');
      return;
    }

    const newOrder = pipeline.filter(p => !p._isDeleted).length;
    const newEntry = {
      _tempId: `temp_${Date.now()}`, // Temporary ID for React keys
      _isNew: true,
      processing_rule_id: ruleId,
      execution_order: newOrder,
      is_active: true,
      rule
    };

    const newPipeline = [...pipeline, newEntry];
    setPipeline(newPipeline);
    setSelectedRuleId('');

    if (onPipelineChange) {
      onPipelineChange(newPipeline);
    }
  };

  const removeFromPipeline = (index) => {
    const newPipeline = pipeline.filter((_, i) => i !== index);

    // Reorder remaining items
    const reorderedPipeline = newPipeline.map((p, i) => ({
      ...p,
      execution_order: i
    }));

    setPipeline(reorderedPipeline);

    if (onPipelineChange) {
      onPipelineChange(reorderedPipeline);
    }
  };

  const moveRule = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= pipeline.length) return;

    const newPipeline = [...pipeline];
    [newPipeline[index], newPipeline[newIndex]] = [newPipeline[newIndex], newPipeline[index]];

    // Update execution orders
    const reorderedPipeline = newPipeline.map((p, i) => ({
      ...p,
      execution_order: i
    }));

    setPipeline(reorderedPipeline);

    if (onPipelineChange) {
      onPipelineChange(reorderedPipeline);
    }
  };

  const toggleActive = (index) => {
    const newPipeline = [...pipeline];
    newPipeline[index] = { ...newPipeline[index], is_active: !newPipeline[index].is_active };
    setPipeline(newPipeline);

    if (onPipelineChange) {
      onPipelineChange(newPipeline);
    }
  };

  const getRuleTypeColor = (ruleType) => {
    const colorMap = {
      'validation': 'bg-blue-100 text-blue-800',
      'transformation': 'bg-purple-100 text-purple-800',
      'normalization': 'bg-green-100 text-green-800',
      'enrichment': 'bg-orange-100 text-orange-800'
    };
    return colorMap[ruleType] || 'bg-gray-100 text-gray-800';
  };

  const activePipeline = pipeline.filter(p => !p._isDeleted);
  const pendingChanges = hasChanges();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">Processing Rules Pipeline</Label>
          {pendingChanges && (
            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
              Unsaved changes
            </Badge>
          )}
        </div>
        <span className="text-xs text-slate-500">
          Rules execute in order from top to bottom
        </span>
      </div>

      {/* Add Rule Section */}
      {!disabled && (
        <div className="flex gap-2">
          <Select value={selectedRuleId} onValueChange={setSelectedRuleId}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select a rule to add..." />
            </SelectTrigger>
            <SelectContent>
              {availableRules
                .filter(r => !activePipeline.some(p => p.processing_rule_id === r.id))
                .map(rule => (
                  <SelectItem key={rule.id} value={String(rule.id)}>
                    <div className="flex items-center gap-2">
                      <span>{rule.rule_name}</span>
                      <Badge className={`${getRuleTypeColor(rule.rule_type)} text-xs`}>
                        {rule.rule_type}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              const rule = availableRules.find(r => r.id === parseInt(selectedRuleId, 10));
              if (rule) setViewingRule(rule);
            }}
            disabled={!selectedRuleId}
            size="sm"
            title="Preview rule code"
          >
            <Code className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            onClick={addRuleToPipeline}
            disabled={!selectedRuleId}
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      )}

      {/* Pipeline List */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="text-center py-4 text-slate-500">Loading pipeline...</div>
        ) : activePipeline.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-6 text-center text-slate-500 text-sm">
              No processing rules in pipeline. Add rules above to create a processing pipeline.
            </CardContent>
          </Card>
        ) : (
          activePipeline.map((item, index) => (
            <Card
              key={item.id || item._tempId}
              className={`border ${item.is_active ? 'border-slate-200' : 'border-slate-200 bg-slate-50 opacity-60'} ${item._isNew ? 'border-l-4 border-l-green-400' : ''}`}
            >
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-3">
                  {/* Drag Handle / Order */}
                  <div className="flex flex-col items-center text-slate-400 w-6">
                    <span className="text-xs font-mono">{index + 1}</span>
                  </div>

                  {/* Rule Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 truncate">
                        {item.rule?.rule_name || 'Unknown Rule'}
                      </span>
                      <Badge className={`${getRuleTypeColor(item.rule?.rule_type)} text-xs`}>
                        {item.rule?.rule_type || 'unknown'}
                      </Badge>
                      {!item.is_active && (
                        <Badge variant="outline" className="text-xs">
                          Disabled
                        </Badge>
                      )}
                      {item._isNew && (
                        <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                          New
                        </Badge>
                      )}
                    </div>
                    {item.rule?.description && (
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {item.rule.description}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    {/* View Code Button - always visible */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewingRule(item.rule)}
                      className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                      title="View rule code"
                    >
                      <Code className="w-4 h-4" />
                    </Button>
                    {!disabled && (
                      <>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveRule(index, 'up')}
                          disabled={index === 0}
                          className="h-8 w-8 p-0"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveRule(index, 'down')}
                          disabled={index === activePipeline.length - 1}
                          className="h-8 w-8 p-0"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActive(index)}
                          className={`h-8 w-8 p-0 ${item.is_active ? 'text-green-600' : 'text-slate-400'}`}
                          title={item.is_active ? 'Disable rule' : 'Enable rule'}
                        >
                          {item.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFromPipeline(index)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {activePipeline.length > 0 && (
        <p className="text-xs text-slate-500">
          {activePipeline.filter(p => p.is_active).length} active rule(s) will be executed in sequence.
          {pendingChanges && " Changes will be saved when you update the version."}
        </p>
      )}

      {/* View Rule Modal */}
      <ViewProcessingRuleModal
        isOpen={!!viewingRule}
        onClose={() => setViewingRule(null)}
        rule={viewingRule}
      />
    </div>
  );
});

ProcessingRulesPipeline.displayName = 'ProcessingRulesPipeline';

export default ProcessingRulesPipeline;
